import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as SummaryActions from './summary.actions';
import {catchError, map, mergeMap, switchMap, takeUntil} from 'rxjs/operators';
import {of} from 'rxjs';
import {SummaryService} from './summary.service';

@Injectable()
export class SummaryEffects {

  private readonly summaryService: SummaryService = inject(SummaryService);
  private readonly actions$: Actions = inject(Actions);


  loadProjectSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SummaryActions.loadProjectSummary),
      mergeMap(({projectId, summaryId}) =>
        this.summaryService.getProjectSummaryById(summaryId, projectId).pipe(
          map(summary =>
            SummaryActions.loadProjectSummarySuccess({summary, projectId})
          ),
          catchError((error) => {
            const notFound = error.status === 404;
            return of(SummaryActions.loadProjectSummaryFailure({error: error.message, notFound, projectId}));
          })
        )
      )
    )
  );

  loadProjectSummaries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SummaryActions.loadProjectSummaries),
      mergeMap(({projectId}) =>
        this.summaryService.getProjectSummaries(projectId).pipe(
          map(summaries =>
            SummaryActions.loadProjectSummariesSuccess({summaries, projectId})
          ),
          catchError((error) => {
            const notFound = error.status === 404;
            return of(SummaryActions.loadProjectSummariesFailure({error: error.message, notFound, projectId}));
          })
        )
      )
    )
  );
  deleteSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SummaryActions.deleteSummary),
      mergeMap(({projectId, summaryId}) =>
        this.summaryService.deleteSummary(projectId, summaryId).pipe(
          map(() =>
            SummaryActions.deleteSummarySuccess({projectId, summaryId})
          ),
          catchError((error) =>
            of(SummaryActions.deleteSummaryFailure({error: error.message, projectId}))
          )
        )
      )
    )
  );

  generateProjectSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SummaryActions.generateProjectSummary),
      mergeMap(({projectId, summary}) =>
        this.summaryService.generateProjectSummary(projectId, summary).pipe(
          map(responseData =>
            SummaryActions.generateProjectSummarySuccess({summary: responseData, projectId})
          ),
          catchError((error) =>
            of(SummaryActions.generateProjectSummaryFailure({error: error.message, projectId}))
          ),
          takeUntil(this.actions$.pipe(ofType(SummaryActions.generateProjectSummaryComplete))),
        )
      )
    )
  );

  generatePaperSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SummaryActions.generatePaperSummary),
      mergeMap(({projectId, paperId}) =>
        this.summaryService.getPaperSummary(projectId, paperId).pipe(
          map(summary =>
            SummaryActions.generatePaperSummarySuccess({summary, paperId})
          ),
          catchError(error => of(SummaryActions.generatePaperSummaryFailure({error: error, paperId})))
        )
      )
    )
  );

  stopGenerateProjectSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SummaryActions.stopGenerateProjectSummary),
      switchMap(action =>
        this.summaryService.stopSummaryProcess(action.projectId).pipe(
          map(() => SummaryActions.stopGenerateProjectSummarySuccess()),
          catchError(error => of(SummaryActions.stopGenerateProjectSummaryFailure({error: error.message})))
        )
      )
    )
  );

  checkGenerateProjectSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SummaryActions.checkGenerateProjectSummaryStart),
      switchMap(action =>
        this.summaryService.isSummaryProcessRunning(action.projectId).pipe(
          map(response => {
            if (response) {
              return SummaryActions.generateProjectSummary({
                projectId: action.projectId, summary: null!
              });
            }
            return SummaryActions.generateProjectSummaryNotRunning();
          }),
          catchError(error => of(SummaryActions.generateProjectSummaryNotRunning()))
        )
      )
    )
  );
}
